import React, { useCallback, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Copy, TriangleAlert } from "lucide-react"
import { Profile } from "@/reducers/profilesSlice"
import { Trans, useTranslation } from "react-i18next"

interface ResetPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  user: Profile
}

type Stage = "confirm" | "reveal"

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>("confirm")
  const [submitting, setSubmitting] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState("")
  const [copied, setCopied] = useState(false)

  const handleClose = useCallback(() => {
    setStage("confirm")
    setGeneratedPassword("")
    setCopied(false)
    onClose()
  }, [onClose])

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      const password = await AdHocCommands.changePassword(
        window.XMPP!,
        user.jid,
      )
      setGeneratedPassword(password)
      setStage("reveal")
    } catch (e: any) {
      const errorText = e?.text ?? e?.message ?? t("fleet.form.unknownError")
      window.appToaster.show({
        message: errorText,
        intent: "danger",
        timeout: 3000,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = useCallback(() => {
    if (!generatedPassword) return
    navigator.clipboard.writeText(generatedPassword).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [generatedPassword])

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.name

  return (
    <Dialog
      open={isOpen}
      onOpenChange={stage === "reveal" ? undefined : handleClose}
    >
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        {stage === "confirm" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {t("fleet.resetPassword.title")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <Trans
                    i18nKey="fleet.resetPassword.warning"
                    values={{ name: displayName }}
                    components={{ b: <strong /> }}
                  />
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="fleet.resetPassword.confirm"
                  values={{ jid: user.jid }}
                  components={{
                    jid: <span className="font-medium text-foreground" />,
                  }}
                />
              </p>
            </div>

            <DialogFooter className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {t("fleet.resetPassword.submit")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {t("fleet.resetPassword.doneTitle")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("fleet.password.onlyOnce")}</span>
              </div>

              <div className="space-y-2">
                <Label>{t("fleet.resetPassword.newPassword")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={generatedPassword}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                    title={t("fleet.password.copyToClipboard")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-muted-foreground">
                    {t("fleet.password.copied")}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={handleClose}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("common.done")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ResetPasswordModal
